/* eslint-disable */
/**
 * The sanitizer only ever touches `doc.body` — walking child nodes, reading
 * tag names/attributes/inline styles. This is the entire surface it needs
 * from a DOM implementation, which is what makes it portable: browsers have
 * it built in (DOMParser), and Node needs something that implements the same
 * narrow shape.
 */
export interface ParsedDoc {
  body: {
    childNodes: ArrayLike<unknown>;
    querySelector(selector: string): unknown;
    textContent: string | null;
  };
}

export type ParseHtml = (html: string) => ParsedDoc;

/**
 * Zero-config default for browser consumers — uses the native DOMParser.
 * Throws with a clear message if called where no DOM exists (e.g. Node),
 * pointing at createNodeDomAdapter instead of failing on a cryptic
 * "DOMParser is not defined".
 */
export const browserParseHtml: ParseHtml = (html: string) => {
  if (typeof DOMParser === "undefined") {
    throw new Error(
      "No DOM available in this environment. Pass a Node adapter explicitly: " +
        "sanitizeEmailHtml(html, { parseHtml: createNodeDomAdapter(parseHTML) }) " +
        "— using the `parseHTML` export from the `linkedom` package (install it yourself; " +
        "this library does not bundle it, so browser builds never pay for it)."
    );
  }
  const doc = new DOMParser().parseFromString(html, "text/html");
  return { body: doc.body as unknown as ParsedDoc["body"] };
};

/**
 * Build a ParseHtml adapter from linkedom's `parseHTML` export (or any
 * lookalike DOM implementation exposing the same shape) via dependency
 * injection — the caller supplies it, so `linkedom` never has to be a
 * dependency of this package at all, and browser bundles are unaffected.
 *
 * Usage (Node only):
 *   import { parseHTML } from 'linkedom';
 *   import { sanitizeEmailHtml, createNodeDomAdapter } from '@kpjayanth/email-builder/core';
 *   const adapter = createNodeDomAdapter(parseHTML);
 *   sanitizeEmailHtml(html, { parseHtml: adapter });
 */
export function createNodeDomAdapter(
  parseHTML: (html: string) => { document: unknown }
): ParseHtml {
  return (html: string) => {
    const { document } = parseHTML(
      `<!doctype html><html><body>${html}</body></html>`
    );
    return { body: (document as { body: unknown }).body as ParsedDoc["body"] };
  };
}
